import { productNames, type NotificationAdapter, type NoticePage, type NoticeRequest, type NoticeSummary } from '@beyond-js/ui/react';
import { type Client, Refusal } from '@accounts/transport';

interface Summary {
	unread: number;
	more?: boolean;
	sources?: { product: string; state: 'available' | 'unavailable' }[];
}

/** What the relay reads when the inbox cannot be read: never a count */
interface Unavailable {
	available: false;
	reason: 'not_configured' | 'projects_unavailable' | (string & {});
}

interface Page {
	items: {
		id: string;
		product: string;
		kind?: string;
		title: string;
		summary?: string;
		occurred: string;
		read?: string;
		group?: string;
		resolved?: string;
		updates?: { count: number; unread: number; more?: boolean };
	}[];
	next?: string;
	sources?: Summary['sources'];
	/** `matter` when Projects answered a page of matters, each item with its `updates` */
	by?: 'update' | 'matter';
}

/**
 * The signed-in person's notifications, read through this service's relay.
 *
 * The inbox belongs to Projects, which aggregates every product's notices; this service forwards the
 * calls with the person's session, which the browser never sees. This class shapes the answers for the
 * shared notification components: an absent or failing Projects is `available: false` — shown as
 * "unavailable", never as an empty inbox. Projects' `more` (the count stopped at its bound, shown
 * "N+") and `sources` (a product whose state is `unavailable` is named as partial) pass through.
 * Nothing here marks anything done: reading is not approving.
 *
 * Matters (X-07, D80): the inbox asks for a page by matter (`by=matter`, one item per `(product, group)` with
 * its `updates`), and opening or marking a matter reads or unreads every update of it on the server
 * (`readGroup`, `unreadGroup`: `{product, group}`), the updates no page loaded included, so the bell's
 * count and the inbox agree.
 *
 * The relay states why the inbox cannot be read, as `200 {available: false, reason}`. With
 * `not_configured` nothing changes until the service is reconfigured, so this adapter — one per page
 * load — never asks again and answers every later count and page as unavailable itself. With
 * `projects_unavailable` the count is asked again only after `RETRY`, whatever the entry's interval;
 * opening the inbox still asks. A readable answer ends either. Every unavailable answer carries that reason, which the components say (`@beyond-js/ui` 0.7.6); when
 * this service itself does not answer there is no reason, and they say only that notifications are unavailable.
 *
 * A read the page's session withheld (a session that ended, which its dialog answers) returns what was
 * last read for it, so the count and the list stay as they were rather than turning into a failure.
 *
 * A change of what the person has read, made through any surface of this interface (the full inbox's
 * Mark as read, an opened item), is heard by `listen`, so the bell's count follows it at once rather than
 * on its next minute.
 */
export class Inbox implements NotificationAdapter {
	/**
	 * The products whose notices reach the inbox, by the family's names (`productNames` of `@beyond-js/ui`,
	 * never translated): the inbox's product filter offers these and no others, so the Desktop and Docs,
	 * which produce no notices, never become filter options. Workspace stays while its retired estate runs:
	 * its administration still keeps a notification outbox (`workspace-2026/administration/src/notifications/`).
	 */
	static products: Readonly<Record<string, string>> = Object.freeze(
		Object.fromEntries(['accounts', 'projects', 'workspace', 'delegate', 'cdn', 'snapshots', 'conduict'].map(id => [id, productNames[id]]))
	);

	/** How long a count stays unavailable before it is asked again while Projects does not answer */
	static RETRY = 300000;

	#client: Client;
	#locale: () => string;
	#unavailable: { reason: string; until: number } | null = null;
	// What was last read, kept for a read the page's session withheld (`quiet`): the count and the page stay
	#summary: NoticeSummary | null = null;
	#pages = new Map<string, NoticePage>();
	#listeners = new Set<() => void>();

	/**
	 * @param locale The interface language at the moment of each call, so text arrives in it
	 */
	constructor(client: Client, locale: () => string) {
		this.#client = client;
		this.#locale = locale;
	}

	/**
	 * @returns Whether an error means the inbox cannot be reached at all, rather than a refusal
	 */
	static #absent(error: unknown): boolean {
		return Refusal.is(error) && error.unreachable;
	}

	/** Why the inbox was last unavailable, or null while it is readable */
	get reason(): string | null {
		return this.#unavailable?.reason ?? null;
	}

	/**
	 * @returns Whether the last answer still stands for this read: always for `not_configured`, until
	 * `RETRY` has passed for anything else, and never for an opened inbox that is not `not_configured`
	 */
	#held(count: boolean): boolean {
		const held = this.#unavailable;
		if (!held) return false;
		return held.reason === 'not_configured' || (count && Date.now() < held.until);
	}

	/**
	 * Records what the relay answered: a reason is kept, a readable answer clears it
	 */
	#note<T extends object>(answer: T | Unavailable): answer is Unavailable {
		if (!('available' in answer) || answer.available !== false) {
			this.#unavailable = null;
			return false;
		}
		this.#unavailable = { reason: String(answer.reason ?? 'projects_unavailable'), until: Date.now() + Inbox.RETRY };
		return true;
	}

	async summary(): Promise<NoticeSummary> {
		if (this.#held(true)) return { available: false, unread: null, reason: this.reason };
		try {
			const read = await this.#client.get<Summary | Unavailable>(`/v1/notifications/summary?locale=${encodeURIComponent(this.#locale())}`);
			if (this.#note(read)) return { available: false, unread: null, reason: this.reason };
			this.#summary = { available: true, unread: read.unread, more: read.more === true, sources: read.sources ?? [] };
			return this.#summary;
		} catch (error) {
			if (Inbox.#absent(error)) return { available: false, unread: null };
			if (Refusal.is(error) && error.quiet && this.#summary) return this.#summary;
			throw error;
		}
	}

	async list(request: NoticeRequest): Promise<NoticePage> {
		const query = new URLSearchParams({ state: request.state, limit: String(request.limit), locale: this.#locale() });
		if (request.product) query.set('product', request.product);
		if (request.by === 'matter') query.set('by', 'matter');
		if (typeof request.cursor === 'string' && request.cursor) query.set('cursor', request.cursor);
		if (this.#held(false)) return { available: false, items: [], reason: this.reason };
		try {
			const page = await this.#client.get<Page | Unavailable>(`/v1/notifications?${query}`);
			if (this.#note(page)) return { available: false, items: [], reason: this.reason };
			const shaped: NoticePage = {
				available: true,
				items: page.items.map(item => ({ ...item, read: item.read ?? null, summary: item.summary ?? null, group: item.group ?? null, resolved: item.resolved ?? null, updates: Inbox.#updates(item.updates) })),
				next: page.next,
				sources: page.sources ?? [],
				...(page.by === 'matter' ? { by: 'matter' as const } : {})
			};
			this.#pages.set(query.toString(), shaped);
			return shaped;
		} catch (error) {
			if (Inbox.#absent(error)) return { available: false, items: [] };
			const kept = this.#pages.get(query.toString());
			if (Refusal.is(error) && error.quiet && kept) return kept;
			throw error;
		}
	}

	/** A matter's `updates` as Projects answers them, or null */
	static #updates(updates: unknown): { count: number; unread: number; more?: boolean } | null {
		const value = updates as { count?: unknown; unread?: unknown; more?: unknown } | null | undefined;
		if (!value || typeof value !== 'object' || !Number.isInteger(value.count)) return null;
		return { count: value.count as number, unread: Number.isInteger(value.unread) ? (value.unread as number) : 0, ...(value.more === true ? { more: true } : {}) };
	}

	/** Called after the person's read state changed here. @returns A function that stops listening */
	listen(listener: () => void): () => void {
		this.#listeners.add(listener);
		return () => void this.#listeners.delete(listener);
	}

	async #changed<T>(work: Promise<T>): Promise<T> {
		const answer = await work;
		for (const listener of this.#listeners) listener();
		return answer;
	}

	read(target: string[] | { before: string; product?: string }): Promise<unknown> {
		return this.#changed(this.#client.post('/v1/notifications/read', Array.isArray(target) ? { ids: target } : target));
	}

	unread(ids: string[]): Promise<unknown> {
		return this.#changed(this.#client.post('/v1/notifications/unread', { ids }));
	}

	/** Reads every update of one matter, loaded or not */
	readGroup({ product, group }: { product: string; group: string }): Promise<unknown> {
		return this.#changed(this.#client.post('/v1/notifications/read', { product, group }));
	}

	/** Makes every update of one matter unread again */
	unreadGroup({ product, group }: { product: string; group: string }): Promise<unknown> {
		return this.#changed(this.#client.post('/v1/notifications/unread', { product, group }));
	}

	/**
	 * Asks again whether the item may be opened, which marks it read. An item that is gone, denied or
	 * someone else's rejects with `code: 'NOT_FOUND'`, as the components expect.
	 */
	async open(id: string): Promise<{ destination: string | null }> {
		const opened = await this.#changed(
			this.#client.post<{ destination: string }>(`/v1/notifications/${encodeURIComponent(id)}/open?locale=${encodeURIComponent(this.#locale())}`)
		);
		return { destination: opened.destination };
	}
}
